import { useCallback, useMemo } from 'react';
import { usePageLoading } from './motion/PetalLoader';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import PageBackHeader from './ui/PageBackHeader';
import Button from './ui/Button';
import GroupLineupVotesList from './lineup/GroupLineupVotesList';
import useGroupInfoPage from '../hooks/useGroupInfoPage';
import { useEventLineup, useGroupMemberVoters } from '../hooks/queries/useEventLineup';
import { isFestivalEvent } from '../utils/eventType';
import { goBackTo } from '../lib/navHistory';

import { usePromptAuth } from './auth/AuthPrompt';
export default function GroupLineupPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const promptAuth = usePromptAuth();
  const info = useGroupInfoPage(groupId);

  const eventId = info.event?.event_id;
  // The Lineup button only renders for festivals now, but the route is still
  // reachable by a stale link or the back stack, so gate the page too rather
  // than trusting the only entry point to stay the only entry point.
  const isFestival = isFestivalEvent(info.event);
  const { data: lineup = [], isLoading: lineupLoading } = useEventLineup(eventId, {
    enabled: Boolean(eventId) && isFestival,
  });
  const { data: votersByArtist = {}, isLoading: votersLoading } = useGroupMemberVoters(
    groupId,
    eventId,
    info.members,
    info.blockedUsers,
    { enabled: Boolean(eventId && info.members.length) },
  );
  const pageLoad = usePageLoading(Boolean(info.session) && (info.loading || lineupLoading), {
    latch: Boolean(info.session),
  });

  const totalMemberCount = useMemo(
    () => info.members.filter((m) => !m.left_at && !info.blockedUsers.has(m.user_id)).length,
    [info.members, info.blockedUsers],
  );

  const blockedMemberCount = useMemo(
    () => info.members.filter((m) => !m.left_at && info.blockedUsers.has(m.user_id)).length,
    [info.members, info.blockedUsers],
  );

  // replace: true so returning to info doesn't leave this page on the stack for
  // the OS swipe-back gesture to land on.
  const handleBack = useCallback(() => {
    goBackTo(navigate, `/groups/${groupId}/info`);
  }, [navigate, groupId]);

  if (!info.session) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center">
        <PageBackHeader title="Lineup" onBack={handleBack} backLabel="Back to group info" />
        <p className="mb-6 text-sm text-ink/60">Log in to view the lineup.</p>
        <Button size="lg" onClick={() => promptAuth()}>
          Log in
        </Button>
      </div>
    );
  }

  if (pageLoad.show) return pageLoad.node; // only the loader until the page can show whole (Sam, 2026-10-20)

  if (info.error) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-6">
        <PageBackHeader title="Lineup" onBack={handleBack} backLabel="Back to group info" />
        <div className="rounded-lg border border-danger-ring bg-danger-fill p-4 text-danger">
          {info.error}
        </div>
      </div>
    );
  }

  // One set-times page (operator, 2026-09-23): a group's lineup is the Sets page with this group picked - same order,
  // b2b rows and picks. This route stays for old links and forwards.
  if (isFestival && eventId) {
    return (
      <Navigate
        replace
        to={`/sets/${encodeURIComponent(eventId)}?group=${encodeURIComponent(groupId)}`}
        state={{ backTo: `/groups/${groupId}/info` }}
      />
    );
  }

  if (!isFestival || lineup.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-6">
        <PageBackHeader title="Lineup" onBack={handleBack} backLabel="Back to group info" />
        <p className="py-12 text-center text-sm text-ink/60">
          {isFestival
            ? 'No lineup for this event yet.'
            : 'Lineups are only available for festivals.'}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      <PageBackHeader title="Lineup" onBack={handleBack} backLabel="Back to group info" />
      <p className="mb-4 lineup-desc">Group members who want to see each artist. Tap to see who.</p>

      {votersLoading ? (
        <div className="py-8 text-center text-sm text-ink/60">Loading...</div>
      ) : (
        <GroupLineupVotesList
          lineup={lineup}
          eventId={eventId}
          groupId={groupId}
          votersByArtist={votersByArtist}
          viewerId={info.userId}
          totalMemberCount={totalMemberCount}
          showBlockedFootnote={blockedMemberCount > 0}
        />
      )}
    </div>
  );
}
